import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trackAdminAction } from '@/lib/analytics';
import { Plus, Mail, Shield, CheckCircle, XCircle, Trash2, Settings, Users, Edit2, UserCog, UserPlus, Key, Copy, Send, Glasses } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AdminDataTable, Column, Action, BulkAction } from '@/components/settings/AdminDataTable';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { EntityAuditTimeline } from '@/components/settings/EntityAuditTimeline';
import { AuditLogLink } from '@/components/settings/AuditLogLink';
import { PermissionGate } from '@/components/settings/PermissionGate';
import { SettingsFilterBar } from '@/components/settings/SettingsFilterBar';
import { useSettingsFilters } from '@/contexts/SettingsFilterContext';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  getAdminUsers,
  createAdminUser,
  updateAdminUser,
  getAdminCompanys,
  getAdminGroups,
  getAdminInvitations,
  createInvitation,
  resendInvitation,
  revokeInvitation,
  deleteAdminUser,
  updateMembership,
  createMembership,
  deleteMembership,
  resetAdminUserPassword,
  createAdminGroup,
  updateAdminGroup,
  deleteAdminGroup,
  type Invitation as InvitationType,
} from '@/lib/adminApi';
import { toast } from 'react-toastify';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { useLabels } from '@/hooks/useLabels';
import { CallsignsField } from '@/components/settings/CallsignsField';
import { callsignProblem, formatCallsigns, parseCallsigns } from '@/lib/callsigns';
import { isCompanyLevelRole } from '@/lib/rbac';

// Minimal company shape used by the settings dropdowns + industry display
interface CompanyOption {
  id: string;
  name: string;
  industry?: string;
}

// Minimal group shape used by the group/team dropdowns + team table
interface GroupOption {
  id: string;
  name: string;
  code?: string;
  company_id: string;
  company_name?: string;
  parent_group_id?: string | null;
  callsigns?: string[];
}

// Helper to group companies by industry for organized dropdowns
const groupCompaniesByIndustry = (companies: CompanyOption[]) => {
  const byIndustry: Record<string, CompanyOption[]> = {};
  companies.forEach(company => {
    const industry = company.industry || 'Other';
    if (!byIndustry[industry]) byIndustry[industry] = [];
    byIndustry[industry].push(company);
  });
  // Sort industries alphabetically, but put 'Other' last
  const sortedIndustries = Object.keys(byIndustry).sort((a, b) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });
  return { byIndustry, sortedIndustries };
};

// Helper to render companies dropdown with industry grouping
const renderCompanyOptions = (companies: CompanyOption[]) => {
  const { byIndustry, sortedIndustries } = groupCompaniesByIndustry(companies);
  return sortedIndustries.map(industry => (
    <div key={industry}>
      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        {industry}
      </div>
      {byIndustry[industry].map((company) => (
        <SelectItem key={company.id} value={company.id} className="pl-4">
          {company.name}
        </SelectItem>
      ))}
    </div>
  ));
};

// Helper to render groups dropdown with parent/child hierarchy
const renderGroupOptions = (groups: GroupOption[], filterByCompanyId?: string) => {
  const filtered = filterByCompanyId
    ? groups.filter((g) => g.company_id === filterByCompanyId)
    : groups;

  // Get parent groups (no parent_group_id)
  const parentGroups = filtered.filter((g) => !g.parent_group_id);

  return parentGroups.map((parentGroup) => {
    const children = filtered.filter((g) => g.parent_group_id === parentGroup.id);
    return (
      <div key={parentGroup.id}>
        <SelectItem value={parentGroup.id} className="font-medium">
          {parentGroup.name}
        </SelectItem>
        {children.map((childGroup) => (
          <SelectItem key={childGroup.id} value={childGroup.id} className="pl-6">
            └ {childGroup.name}
          </SelectItem>
        ))}
      </div>
    );
  });
};

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  job_title?: string | null;
  is_internal: boolean;
  /** T-018: explicit per-employee grant to operate wearables (glasses glyph). */
  wearables_enabled?: boolean;
  created_at: string;
  default_company_id?: string;
  default_group_id?: string;
  memberships: Array<{
    id: string;
    company_id: string;
    company_name: string;
    group_id?: string;
    group_name?: string;
    role: string;
  }>;
}

// Helper to get display name from first/last name
const getDisplayName = (user: User | null | undefined): string => {
  if (!user) return 'Unknown';
  const parts = [user.first_name, user.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : user.email;
};

// Use InvitationType from adminApi for invitations
type Invitation = InvitationType;

// Role hierarchy (higher number = higher privilege)
// MUST match backend api/routes/admin/users.js roleHierarchy
const ROLE_HIERARCHY: Record<string, number> = {
  'user': 1,
  'group_admin': 2,
  'company_admin': 3,
  'admin': 4,
  'superadmin': 5,
};

// Role display labels
const ROLE_LABELS: Record<string, string> = {
  'user': 'User (Read Only)',
  'group_admin': 'Group Admin',
  'company_admin': 'Company Admin',
  'admin': 'Internal Admin',
  'superadmin': 'Superadmin',
};

// Short role labels for compact displays
const ROLE_LABELS_SHORT: Record<string, string> = {
  'user': 'User',
  'group_admin': 'Group Admin',
  'company_admin': 'Company Admin',
  'admin': 'Admin',
  'superadmin': 'Superadmin',
};

export function AdminUsers() {
  const { getLabel } = useLabels();
  const { user: currentUser, is_superadmin, memberships, permissions, loading: settingsLoading } = useSettingsContext();
  const [searchParams, setSearchParams] = useSearchParams();
  // T-xxx Teams-tab CRUD: a "team" is a root group. Reuse the group permissions.
  const canPerm = (p: string) => permissions.includes(p) || permissions.includes('platform.all');

  // Calculate current user's highest role level
  const currentUserHighestRoleLevel = is_superadmin
    ? ROLE_HIERARCHY['superadmin']
    : Math.max(...(memberships || []).map(m => ROLE_HIERARCHY[m.role] || 0), 0);

  // Get available roles based on current user's role level
  // Users can only assign roles BELOW their own level
  const getAvailableRoles = (includeNoChange = false): { value: string; label: string }[] => {
    const roles: { value: string; label: string }[] = [];

    if (includeNoChange) {
      roles.push({ value: '__no_change__', label: 'No change' });
    }

    // Only show roles that are STRICTLY BELOW the current user's role level
    Object.entries(ROLE_HIERARCHY).forEach(([role, level]) => {
      if (level < currentUserHighestRoleLevel) {
        roles.push({ value: role, label: ROLE_LABELS[role] });
      }
    });

    return roles;
  };

  // Get available roles for short labels (membership management)
  const getAvailableRolesShort = (): { value: string; label: string }[] => {
    const roles: { value: string; label: string }[] = [];

    Object.entries(ROLE_HIERARCHY).forEach(([role, level]) => {
      if (level < currentUserHighestRoleLevel) {
        roles.push({ value: role, label: ROLE_LABELS_SHORT[role] });
      }
    });

    return roles;
  };

  // Settings-specific filters (independent from Map page)
  const { selectedIndustries, isLoading: filtersLoading, ensureCompanySelected } = useSettingsFilters();

  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bulkDrawerOpen, setBulkDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('members');
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '', // Only for direct create
    first_name: '', // Only for direct create
    last_name: '', // Only for direct create
    company_id: '',
    group_ids: [] as string[],  // Multi-group support
    role: 'user',
    message: '',  // Personal message
    is_invite: true, // Default to invite
    is_internal: false, // Only for direct create (superadmin)
  });

  // Confirm password state for direct create
  const [createConfirmPassword, setCreateConfirmPassword] = useState('');

  // Verification code modal state (shown after creating invitation)
  const [verificationCodeModal, setVerificationCodeModal] = useState<{
    open: boolean;
    code: string;
    inviteUrl: string;
    email: string;
  }>({
    open: false,
    code: '',
    inviteUrl: '',
    email: '',
  });

  // Bulk edit form state
  const [bulkFormData, setBulkFormData] = useState({
    role: '__no_change__',
    group_id: '__no_change__',
    is_internal: false,
  });

  // Edit user state
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    job_title: '',
    is_internal: false,
    wearables_enabled: false,
    default_company_id: '',
    default_group_id: '',
  });

  // Teams-tab CRUD state (a "team" is a root group). Reuses the group CRUD APIs.
  const [teamDrawerOpen, setTeamDrawerOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<GroupOption | null>(null);
  const [teamForm, setTeamForm] = useState({ name: '', code: '', company_id: '', callsigns: '' });
  const [teamSaving, setTeamSaving] = useState(false);

  // Membership management state
  const [managingUser, setManagingUser] = useState<User | null>(null);
  const [membershipDrawerOpen, setMembershipDrawerOpen] = useState(false);
  const [newMembershipForm, setNewMembershipForm] = useState({
    company_id: '',
    group_id: '',
    role: 'user',
  });

  // Team management state
  const [selectedTeam, setSelectedTeam] = useState<GroupOption | null>(null);
  const [teamMembersDrawerOpen, setTeamMembersDrawerOpen] = useState(false);

  // Password reset state (superadmin only)
  const [passwordResetUser, setPasswordResetUser] = useState<User | null>(null);
  const [passwordResetDrawerOpen, setPasswordResetDrawerOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [addToTeamDrawerOpen, setAddToTeamDrawerOpen] = useState(false);
  const [addToTeamForm, setAddToTeamForm] = useState({
    user_id: '',
    role: 'user',
  });

  // Dynamic Title
  const getPageTitle = () => {
    if (is_superadmin) return 'All Users';
    const role = memberships[0]?.role;
    if (role === 'admin') return 'Staff & Users';
    if (role === 'group_admin') return 'Team Members';
    return 'Users';
  };

  // Reload data when settings filters change - wait for context to initialize first
  // Use JSON.stringify for stable dependency on array contents
  const selectedIndustriesKey = JSON.stringify(selectedIndustries);
  useEffect(() => {
    // Don't load until filters are initialized to prevent double-loading
    if (filtersLoading) return;
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndustriesKey, filtersLoading]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, invitesData, companiesData, groupsData] = await Promise.all([
        getAdminUsers({ industries: selectedIndustries }),
        getAdminInvitations({ industries: selectedIndustries }),
        getAdminCompanys({ industries: selectedIndustries }),
        getAdminGroups({ industries: selectedIndustries }),
      ]);
      setUsers(usersData.users);
      setInvitations(invitesData.invitations);
      setCompanies(companiesData.companies);
      setGroups(groupsData.groups);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load users data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      company_id: '',
      group_ids: [],
      role: 'user',
      message: '',
      is_invite: true,
      is_internal: false,
    });
    setCreateConfirmPassword('');
    setDrawerOpen(true);
  };

  // Deep link (`?new=1`, from Sales' business-card form): open the Add User drawer
  // once permissions are known. The param stays while the drawer is open —
  // SettingsLayout takes this page off screen and mounts it again while labels
  // load, and the drawer has to come back with it — and is dropped when the drawer
  // closes, so a later reload or Back doesn't reopen it. A caller who may not
  // invite just lands on the list.
  const wantsNewUser = searchParams.get('new') === '1';
  const dropNewUserParam = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('new');
      return next;
    }, { replace: true });
  };
  useEffect(() => {
    if (!wantsNewUser || settingsLoading) return;
    if (canPerm('users.invite')) handleCreate();
    else dropNewUserParam();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsNewUser, settingsLoading]);

  const closeCreateDrawer = () => {
    setDrawerOpen(false);
    if (wantsNewUser) dropNewUserParam();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate passwords match for direct create
    if (!formData.is_invite && formData.password !== createConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      if (formData.is_invite) {
        const response = await createInvitation({
          email: formData.email,
          company_id: formData.company_id,
          group_ids: formData.group_ids.length > 0 ? formData.group_ids : undefined,
          role: formData.role,
          message: formData.message || undefined,
        });

        // Show verification code modal
        setVerificationCodeModal({
          open: true,
          code: response.verification_code,
          inviteUrl: response.invite_url,
          email: formData.email,
        });

        // Ensure the invited user's company is included in the filter
        if (formData.company_id) {
          ensureCompanySelected(formData.company_id);
        }

        trackAdminAction('user', 'invited');
        if (response.email_delivery === 'route_disabled') {
          toast.warning('Invitation created, but invitation emails are turned off. Share the link and code shown below — enable the route in Settings → Alerts & Reports to email invites.');
        } else if (response.email_delivery === 'failed') {
          toast.warning('Invitation created, but the email could not be sent. Share the link and code shown below.');
        } else {
          toast.success(response.message || 'Invitation sent successfully');
        }
      } else {
        // Send only the fields the backend expects
        await createAdminUser({
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
          company_id: formData.company_id || undefined,
          group_id: formData.group_ids.length > 0 ? formData.group_ids[0] : undefined,
          role: formData.role,
          is_internal: is_superadmin && formData.is_internal,
        });

        // Ensure the new user's company is included in the filter
        // This prevents the confusing case where a user is created but immediately hidden
        if (formData.company_id) {
          ensureCompanySelected(formData.company_id);
        }

        trackAdminAction('user', 'created');
        toast.success('User created successfully');
      }
      closeCreateDrawer();
      loadData();
    } catch (error: unknown) {
      console.error('Failed to process:', error);
      toast.error((error as Error)?.message || 'Operation failed');
    }
  };

  const handleRevokeInvite = async (invite: Invitation) => {
    try {
      await revokeInvitation(invite.id);
      toast.success('Invitation revoked');
      loadData();
    } catch (_error) {
      toast.error('Failed to revoke invitation');
    }
  };

  const handleResendInvite = async (invite: Invitation) => {
    try {
      const response = await resendInvitation(invite.id);

      // Show verification code modal with new code
      setVerificationCodeModal({
        open: true,
        code: response.verification_code,
        inviteUrl: response.invite_url,
        email: invite.email,
      });

      if (response.email_delivery === 'route_disabled') {
        toast.warning('New code generated, but invitation emails are turned off. Share the link and code shown — enable the route in Settings → Alerts & Reports to email invites.');
      } else if (response.email_delivery === 'failed') {
        toast.warning('New code generated, but the email could not be sent. Share the link and code shown.');
      } else {
        toast.success(response.message || 'Invitation resent successfully');
      }
      loadData();
    } catch (error: unknown) {
      toast.error((error as Error)?.message || 'Failed to resend invitation');
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    try {
      await deleteAdminUser(user.id);
      trackAdminAction('user', 'deleted', user.id);
      toast.success('User deleted');
      loadData();
    } catch (_error) {
      toast.error('Failed to delete user');
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditFormData({
      email: user.email,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      job_title: user.job_title || '',
      is_internal: user.is_internal,
      wearables_enabled: user.wearables_enabled ?? false,
      default_company_id: user.memberships[0]?.company_id || '',
      default_group_id: user.memberships[0]?.group_id || '',
    });
    setEditDrawerOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      // T-018: only send the wearables grant when it actually changed, so an
      // unrelated edit doesn't re-stamp wearables_enabled_at/_by. The backend
      // still enforces that only superadmin/internal may change it.
      const payload: typeof editFormData = { ...editFormData };
      if (payload.wearables_enabled === (editingUser.wearables_enabled ?? false)) {
        delete (payload as Partial<typeof editFormData>).wearables_enabled;
      }
      // Same for the title: an unrelated edit must not re-validate (or rewrite)
      // a title nobody touched. Emptying the field sends '' — the server's clear.
      payload.job_title = payload.job_title.trim();
      if (payload.job_title === (editingUser.job_title ?? '').trim()) {
        delete (payload as Partial<typeof editFormData>).job_title;
      }
      await updateAdminUser(editingUser.id, payload);
      trackAdminAction('user', 'updated', editingUser.id);
      toast.success('User updated successfully');
      setEditDrawerOpen(false);
      setEditingUser(null);
      loadData();
    } catch (error: unknown) {
      console.error('Failed to update user:', error);
      toast.error((error as Error)?.message || 'Failed to update user');
    }
  };

  const handleManageMemberships = (user: User) => {
    setManagingUser(user);
    setNewMembershipForm({
      company_id: user.memberships[0]?.company_id || '',
      group_id: '',
      role: 'user',
    });
    setMembershipDrawerOpen(true);
  };

  const handleAddMembership = async () => {
    if (!managingUser) return;

    try {
      await createMembership({
        user_id: managingUser.id,
        company_id: newMembershipForm.company_id,
        group_id: newMembershipForm.group_id || undefined,
        role: newMembershipForm.role,
      });
      toast.success('Membership added successfully');
      setNewMembershipForm({
        company_id: '',
        group_id: '',
        role: 'user',
      });
      loadData();
    } catch (error: unknown) {
      console.error('Failed to add membership:', error);
      toast.error((error as Error)?.message || 'Failed to add membership');
    }
  };

  const handleUpdateMembershipRole = async (membershipId: string, newRole: string) => {
    try {
      await updateMembership(membershipId, { role: newRole });
      toast.success('Role updated successfully');
      loadData();
    } catch (error: unknown) {
      console.error('Failed to update role:', error);
      toast.error((error as Error)?.message || 'Failed to update role');
    }
  };

  const handleDeleteMembership = async (membershipId: string) => {
    if (!confirm('Remove this membership? The user will lose access to this company/group.')) return;

    try {
      await deleteMembership(membershipId);
      toast.success('Membership removed successfully');
      loadData();
    } catch (error: unknown) {
      console.error('Failed to delete membership:', error);
      toast.error((error as Error)?.message || 'Failed to delete membership');
    }
  };

  // Password reset handlers (superadmin only)
  const handlePasswordReset = (user: User) => {
    setPasswordResetUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordResetDrawerOpen(true);
  };

  // Password complexity validation
  const passwordRequirements = [
    { label: 'At least 8 characters', met: newPassword.length >= 8 },
    { label: 'Contains uppercase letter', met: /[A-Z]/.test(newPassword) },
    { label: 'Contains lowercase letter', met: /[a-z]/.test(newPassword) },
    { label: 'Contains a number', met: /[0-9]/.test(newPassword) },
  ];

  const handlePasswordResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordResetUser) return;

    // Superadmins bypass complexity requirements - just need non-empty + matching
    if (!newPassword) {
      toast.error('Password is required');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      await resetAdminUserPassword(passwordResetUser.id, newPassword);
      toast.success(`Password reset successfully for ${getDisplayName(passwordResetUser)}`);
      setPasswordResetDrawerOpen(false);
      setPasswordResetUser(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: unknown) {
      console.error('Failed to reset password:', error);
      toast.error((error as Error)?.message || 'Failed to reset password');
    }
  };

  // Columns for Users
  const userColumns: Column<User>[] = [
    {
      key: 'email',
      label: 'User',
      sortable: true,
      render: (user) => (
        <div className="flex flex-col">
          <span className="font-medium text-foreground">{getDisplayName(user)}</span>
          <span className="text-xs text-muted-foreground">{user.email}</span>
        </div>
      ),
    },
    {
      key: 'company',
      label: 'Company',
      render: (user) => (
        <div className="flex flex-col gap-1">
          {user.memberships.map((m, i) => (
            <div key={i} className="flex items-center gap-1">
              <span className="text-sm text-foreground">
                {m.company_name}
              </span>
              {user.default_company_id === m.company_id && (
                <Badge className="bg-[#8FB560]/20 text-[#8FB560] text-[10px] px-1 py-0">
                  Default
                </Badge>
              )}
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'group',
      label: getLabel('group'),
      render: (user) => (
        <div className="flex flex-col gap-1">
          {user.memberships.map((m, i) => (
            <div key={i} className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground">
                {m.group_name || '—'}
              </span>
              {user.default_group_id && user.default_group_id === m.group_id && (
                <Badge className="bg-[#8FB560]/20 text-[#8FB560] text-[10px] px-1 py-0">
                  Default
                </Badge>
              )}
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      render: (user) => {
        // Check if user has superadmin role at company level (group_id = null)
        const hasSuperadmin = user.memberships.some(
          m => m.role === 'superadmin' && !m.group_id
        );

        if (hasSuperadmin) {
          // Superadmin supersedes all - show single badge
          return (
            <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30 w-fit">
              Superadmin
            </Badge>
          );
        }

        // Show individual roles for non-superadmins
        return (
          <div className="flex flex-col gap-1">
            {user.memberships.map((m, i) => (
              <Badge key={i} variant="outline" className="bg-muted text-foreground border-border w-fit whitespace-nowrap">
                {ROLE_LABELS_SHORT[m.role] || m.role.replace('_', ' ')}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      key: 'location',
      label: 'Location',
      render: (user) => (
        <div className="flex flex-col gap-1">
          {user.memberships.map((m, i) => {
            // Extract location from group name (e.g., "Unit 2 (London)" -> "London")
            const locationMatch = m.group_name?.match(/\(([^)]+)\)/);
            const location = locationMatch ? locationMatch[1] : '—';
            return (
              <span key={i} className="text-sm text-muted-foreground">
                {location}
              </span>
            );
          })}
        </div>
      ),
    },
    {
      key: 'is_internal',
      label: 'Status',
      render: (user) => (
        <Badge className={user.is_internal ? "bg-purple-500/20 text-purple-400 hover:bg-purple-500/30" : "bg-muted text-foreground"}>
          {user.is_internal ? 'Internal' : 'External'}
        </Badge>
      ),
    },
    {
      key: 'wearables_enabled',
      label: 'Wearables',
      // T-018: the glasses glyph marks an explicit per-employee wearables grant.
      render: (user) => (
        user.wearables_enabled ? (
          <span title="Wearables access granted" className="inline-flex items-center">
            <Glasses className="w-4 h-4" style={{ color: '#8FB560' }} aria-label="Wearables access granted" />
          </span>
        ) : (
          <span className="text-muted-foreground" aria-label="No wearables access">—</span>
        )
      ),
    },
    {
      key: 'created_at',
      label: 'Joined',
      sortable: true,
      render: (user) => new Date(user.created_at).toLocaleDateString(),
    },
  ];

  // Columns for Invitations
  const inviteColumns: Column<Invitation>[] = [
    {
      key: 'email',
      label: 'Invitee',
      sortable: true,
      render: (invite) => (
        <div className="flex flex-col">
          <span className="font-medium text-foreground">{invite.email}</span>
          <span className="text-xs text-muted-foreground">
            {invite.company_name}
            {invite.groups && invite.groups.length > 0 && (
              <> · {invite.groups.map(g => g.group_name).join(', ')}</>
            )}
          </span>
        </div>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      render: (invite) => (
        <Badge variant="outline" className="bg-muted text-foreground border-border w-fit whitespace-nowrap">
          {ROLE_LABELS_SHORT[invite.role] || invite.role.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (invite) => {
        const styles = {
          pending: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
          accepted: 'bg-green-500/10 text-green-400 border-green-500/20',
          expired: 'bg-red-500/10 text-red-400 border-red-500/20',
          revoked: 'bg-muted-foreground/10 text-muted-foreground border-border/20',
        };
        return (
          <div className="flex flex-col gap-1">
            <Badge variant="outline" className={`${styles[invite.status]} capitalize w-fit`}>
              {invite.status}
            </Badge>
            {invite.resend_count > 0 && (
              <span className="text-xs text-muted-foreground">
                Resent {invite.resend_count}x
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'created_at',
      label: 'Sent',
      sortable: true,
      render: (invite) => (
        <div className="flex flex-col text-xs text-muted-foreground">
          <span>{new Date(invite.created_at).toLocaleDateString()}</span>
          <span>by {invite.inviter_name || 'System'}</span>
        </div>
      ),
    },
    {
      key: 'expires_at',
      label: 'Expires',
      render: (invite) => {
        const isExpired = new Date(invite.expires_at) < new Date();
        return (
          <span className={`text-xs ${isExpired ? 'text-red-400' : 'text-muted-foreground'}`}>
            {new Date(invite.expires_at).toLocaleDateString()}
          </span>
        );
      },
    },
  ];

  const userActions: Action<User>[] = [
    {
      label: 'Edit',
      onClick: handleEditUser,
      icon: Edit2,
    },
    {
      label: 'Manage Access',
      onClick: handleManageMemberships,
      icon: UserCog,
    },
    {
      label: 'Reset Password',
      onClick: handlePasswordReset,
      icon: Key,
      show: () => is_superadmin, // Only visible to superadmins
    },
    {
      label: 'Delete',
      onClick: handleDeleteUser,
      variant: 'destructive',
      icon: Trash2,
    },
  ];

  const inviteActions: Action<Invitation>[] = [
    {
      label: 'Resend',
      onClick: handleResendInvite,
      icon: Send,
      show: (invite) => invite.status === 'pending',
    },
    {
      label: 'Revoke',
      onClick: handleRevokeInvite,
      variant: 'destructive',
      icon: XCircle,
      show: (invite) => invite.status === 'pending',
    },
  ];

  const handleBulkEdit = (users: User[]) => {
    setSelectedUsers(users);
    setBulkFormData({
      role: '',
      group_id: '',
      is_internal: false,
    });
    setBulkDrawerOpen(true);
  };

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const promises: Promise<unknown>[] = [];

      // Update is_internal for all users if superadmin
      if (is_superadmin) {
        const profileUpdates = { is_internal: bulkFormData.is_internal };
        promises.push(
          ...selectedUsers.map(user => updateAdminUser(user.id, profileUpdates))
        );
      }

      // Update role for each user's first membership if role is changed
      if (bulkFormData.role && bulkFormData.role !== '__no_change__') {
        promises.push(
          ...selectedUsers
            .filter(user => user.memberships.length > 0)
            .map(user => 
              updateMembership(user.memberships[0].id, { role: bulkFormData.role })
            )
        );
      }

      // Show warning if group change is attempted
      if (bulkFormData.group_id && bulkFormData.group_id !== '__no_change__') {
        toast.warning('Group bulk changes require membership management. Role and status updated.');
      }

      // Execute all updates in parallel
      await Promise.all(promises);

      toast.success(`Successfully updated ${selectedUsers.length} users`);
      setBulkDrawerOpen(false);
      loadData();
    } catch (error: unknown) {
      console.error('Failed to bulk update users:', error);
      toast.error((error as Error)?.message || 'Failed to update users');
    }
  };

  // Apply filters to users
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          user.email?.toLowerCase().includes(searchLower) ||
          getDisplayName(user).toLowerCase().includes(searchLower) ||
          user.memberships.some(m =>
            m.company_name?.toLowerCase().includes(searchLower) ||
            m.group_name?.toLowerCase().includes(searchLower)
          );
        if (!matchesSearch) return false;
      }

      return true;
    });
  }, [users, searchTerm]);

  // Apply filters to invitations
  const filteredInvitations = useMemo(() => {
    return invitations.filter((invite) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          invite.email?.toLowerCase().includes(searchLower) ||
          invite.company_name?.toLowerCase().includes(searchLower) ||
          invite.groups?.some(g => g.group_name?.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }

      return true;
    });
  }, [invitations, searchTerm]);

  // Get teams (root groups with no parent)
  const teams = useMemo(() => {
    return groups.filter((g) => !g.parent_group_id);
  }, [groups]);

  // Filter teams based on search
  const filteredTeams = useMemo(() => {
    if (!searchTerm.trim()) return teams;
    const search = searchTerm.toLowerCase();
    return teams.filter((team) =>
      team.name?.toLowerCase().includes(search) ||
      team.code?.toLowerCase().includes(search) ||
      team.company_name?.toLowerCase().includes(search)
    );
  }, [teams, searchTerm]);

  // Get members of a specific team (users who have membership with this group_id)
  const getTeamMembers = (teamId: string) => {
    return users.filter(user =>
      user.memberships.some(m => m.group_id === teamId)
    );
  };

  // Get users not in a specific team (for adding to team)
  const getUsersNotInTeam = (teamId: string) => {
    return users.filter(user =>
      !user.memberships.some(m => m.group_id === teamId)
    );
  };

  // Handle viewing team members
  const handleViewTeamMembers = (team: GroupOption) => {
    setSelectedTeam(team);
    setTeamMembersDrawerOpen(true);
  };

  // --- Teams CRUD (a team is a root group; reuses the group CRUD APIs + guards) ---
  const handleCreateTeam = () => {
    setEditingTeam(null);
    setTeamForm({ name: '', code: '', company_id: companies[0]?.id || '', callsigns: '' });
    setTeamDrawerOpen(true);
  };

  const handleEditTeam = (team: GroupOption) => {
    setEditingTeam(team);
    setTeamForm({ name: team.name || '', code: team.code || '', company_id: team.company_id, callsigns: formatCallsigns(team.callsigns) });
    setTeamDrawerOpen(true);
  };

  const handleDeleteTeam = async (team: GroupOption) => {
    // Same guard/UX as /settings/groups: confirm, then the server deletes the
    // group. FK behavior (verified against the foundation schema): child sites
    // are DETACHED, not deleted (groups.parent_group_id is ON DELETE SET NULL,
    // so they become top-level); units lose their primary-group assignment
    // (primary_group_id SET NULL); direct memberships + unit-group links cascade.
    if (!confirm(`Delete team "${team.name}"? Its sites will be detached (moved to top level) and its units will lose their team assignment. This can't be undone.`)) return;
    try {
      await deleteAdminGroup(team.id);
      toast.success('Team deleted');
      loadData();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete team');
    }
  };

  const handleTeamSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamForm.name.trim() || !teamForm.company_id) {
      toast.error('Team name and company are required');
      return;
    }
    const callsigns = parseCallsigns(teamForm.callsigns);
    const callsignError = callsignProblem(callsigns);
    if (callsignError) {
      toast.error(callsignError);
      return;
    }
    setTeamSaving(true);
    try {
      if (editingTeam) {
        // Rename only — company is intentionally NOT editable here. Moving a
        // team's company_id alone would strand its child sites/units in the old
        // company (cross-company stale references break group-scoped authz and
        // filters); a cross-tenant move needs a dedicated cascade-aware flow.
        await updateAdminGroup(editingTeam.id, { name: teamForm.name.trim(), code: teamForm.code.trim() || null, callsigns });
        toast.success('Team updated');
      } else {
        // parent_group_id null → a root group (a team).
        await createAdminGroup({ name: teamForm.name.trim(), code: teamForm.code.trim() || null, company_id: teamForm.company_id, parent_group_id: null, callsigns });
        toast.success('Team created');
      }
      setTeamDrawerOpen(false);
      loadData();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save team');
    } finally {
      setTeamSaving(false);
    }
  };

  // Handle opening add to team drawer
  const handleOpenAddToTeam = () => {
    if (!selectedTeam) return;
    setAddToTeamForm({ user_id: '', role: 'user' });
    setAddToTeamDrawerOpen(true);
  };

  // Handle adding user to team
  const handleAddUserToTeam = async () => {
    if (!selectedTeam || !addToTeamForm.user_id) return;

    try {
      await createMembership({
        user_id: addToTeamForm.user_id,
        company_id: selectedTeam.company_id,
        group_id: selectedTeam.id,
        role: addToTeamForm.role,
      });
      toast.success('User added to team');
      setAddToTeamDrawerOpen(false);
      loadData();
    } catch (error: unknown) {
      toast.error((error as Error)?.message || 'Failed to add user to team');
    }
  };

  // Handle removing user from team
  const handleRemoveFromTeam = async (user: User, teamId: string) => {
    const membership = user.memberships.find(m => m.group_id === teamId);
    if (!membership) return;

    if (!confirm(`Remove ${getDisplayName(user)} from this team?`)) return;

    try {
      await deleteMembership(membership.id);
      toast.success('User removed from team');
      loadData();
    } catch (error: unknown) {
      toast.error((error as Error)?.message || 'Failed to remove user from team');
    }
  };

  // Team columns for the table
  const teamColumns: Column<GroupOption>[] = [
    {
      key: 'name',
      label: 'Team Name',
      sortable: true,
      render: (team) => (
        <div className="font-medium text-foreground flex items-center gap-2">
          <Users className="w-4 h-4 text-[#8FB560]" />
          {team.name}
        </div>
      ),
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      render: (team) => (
        <span className="font-mono text-xs bg-card px-2 py-1 rounded border border-border text-muted-foreground">
          {team.code}
        </span>
      ),
    },
    {
      key: 'members',
      label: 'Members',
      render: (team) => {
        const memberCount = getTeamMembers(team.id).length;
        return (
          <span className="text-muted-foreground">
            {memberCount} {memberCount === 1 ? 'member' : 'members'}
          </span>
        );
      },
    },
    {
      key: 'company_name',
      label: 'Company',
      sortable: true,
    },
  ];

  // Team actions. Edit/Delete are gated to the group permissions (server enforces
  // the same scope); Manage Members stays for everyone who reaches the tab.
  const teamActions: Action<GroupOption>[] = [
    {
      label: 'Manage Members',
      onClick: handleViewTeamMembers,
      icon: Users,
    },
    {
      label: 'Edit',
      onClick: handleEditTeam,
      icon: Edit2,
      show: () => canPerm('groups.update'),
    },
    {
      label: 'Delete',
      onClick: handleDeleteTeam,
      icon: Trash2,
      variant: 'destructive',
      show: () => canPerm('groups.delete'),
    },
  ];

  const userBulkActions: BulkAction<User>[] = [
    {
      label: 'Bulk Edit Metadata',
      icon: Settings,
      onClick: handleBulkEdit,
    },
    {
      label: 'Delete Selected',
      icon: Trash2,
      variant: 'destructive',
      onClick: async (selectedUsers) => {
        if (!confirm(`Are you sure you want to delete ${selectedUsers.length} users?`)) return;
        
        try {
          await Promise.all(selectedUsers.map(u => deleteAdminUser(u.id)));
          toast.success(`Deleted ${selectedUsers.length} users`);
          loadData();
        } catch (_error) {
          toast.error('Failed to delete users');
        }
      }
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground whitespace-nowrap">{getPageTitle()}</h1>
              <AuditLogLink resourceTypes={['user']} tooltip="View user audit history" />
            </div>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">Manage access and invitations</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <SettingsFilterBar
              searchQuery={searchTerm}
              onSearchChange={setSearchTerm}
              placeholder="Search by name, email, role, or company..."
            />
            {/* Dynamic action button based on active tab */}
            {activeTab === 'teams' ? (
              <PermissionGate permission="groups.create">
                <Button
                  onClick={handleCreateTeam}
                  className="bg-[#8FB560] hover:bg-[#7a9d51] whitespace-nowrap w-full sm:w-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Team
                </Button>
              </PermissionGate>
            ) : (
              <PermissionGate permission="users.invite">
                <Button onClick={handleCreate} className="bg-[#8FB560] hover:bg-[#7a9d51] whitespace-nowrap w-full sm:w-auto">
                  <Plus className="w-4 h-4 mr-2" />
                  {activeTab === 'invitations' ? 'Invite User' : 'Add User'}
                </Button>
              </PermissionGate>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-card border border-border p-1">
          <TabsTrigger value="members" className="data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            Users ({filteredUsers.length})
          </TabsTrigger>
          <TabsTrigger value="teams" className="data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            Teams ({filteredTeams.length})
          </TabsTrigger>
          <TabsTrigger value="invitations" className="data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            Invites ({filteredInvitations.filter(i => i.status === 'pending').length})
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="members">
            <AdminDataTable
              data={filteredUsers}
              columns={userColumns}
              actions={userActions}
              bulkActions={userBulkActions}
              loading={loading}
              emptyMessage="No active members found"
              tableId="users-members"
            />
          </TabsContent>
          <TabsContent value="teams">
            <AdminDataTable
              data={filteredTeams}
              columns={teamColumns}
              actions={teamActions}
              loading={loading}
              emptyMessage="No teams found"
              tableId="users-teams"
            />
          </TabsContent>
          <TabsContent value="invitations">
            <AdminDataTable
              data={filteredInvitations}
              columns={inviteColumns}
              actions={inviteActions}
              loading={loading}
              emptyMessage="No invitations found"
              tableId="users-invitations"
            />
          </TabsContent>
        </div>
      </Tabs>

      {/* Create/Invite Drawer */}
      <AdminFormDrawer
        open={drawerOpen}
        onClose={closeCreateDrawer}
        title={formData.is_invite ? "Invite New User" : "Create User"}
        description={formData.is_invite ? "Send an email invitation to a new team member" : "Directly create a user profile"}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Toggle Invite/Direct (Superadmin only) */}
          {is_superadmin && (
            <div className="flex items-center space-x-2 bg-card p-3 rounded-lg border border-border">
              <input
                type="checkbox"
                id="is_invite"
                checked={!formData.is_invite}
                onChange={(e) => setFormData({ ...formData, is_invite: !e.target.checked, is_internal: false })}
                className="rounded border-border"
              />
              <Label htmlFor="is_invite" className="font-normal cursor-pointer">
                Create directly (skip email invite)
              </Label>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="colleague@example.com"
                required
                className="bg-background border-border"
              />
            </div>

            {!formData.is_invite && (
              <>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <PasswordInput
                    id="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!formData.is_invite}
                    placeholder="••••••••"
                    className="bg-background"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm_password">Confirm Password</Label>
                  <PasswordInput
                    id="confirm_password"
                    value={createConfirmPassword}
                    onChange={(e) => setCreateConfirmPassword(e.target.value)}
                    required={!formData.is_invite}
                    placeholder="••••••••"
                    className="bg-background"
                    error={createConfirmPassword.length > 0 && formData.password !== createConfirmPassword}
                  />
                  {createConfirmPassword.length > 0 && formData.password !== createConfirmPassword && (
                    <p className="text-sm text-red-500 mt-1">Passwords do not match</p>
                  )}
                  {createConfirmPassword.length > 0 && formData.password === createConfirmPassword && formData.password.length >= 8 && (
                    <div className="flex items-center gap-2 text-sm text-[#8FB560] mt-1">
                      <CheckCircle className="w-3.5 h-3.5" />
                      <span>Passwords match</span>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="bg-background border-border"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="bg-background border-border"
                    />
                  </div>
                </div>
                {/* Internal = Hollywood Trucks staff: full access here, sign-in to Sales,
                    and a place in Sales' business-card picker. The API refuses it from
                    anyone but a superadmin (provisioningDecision). */}
                {is_superadmin && (
                  <div className="bg-card p-3 rounded-lg border border-border">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="is_internal"
                        checked={formData.is_internal}
                        onChange={(e) => setFormData({ ...formData, is_internal: e.target.checked })}
                        className="rounded border-border"
                      />
                      <Label htmlFor="is_internal" className="font-normal cursor-pointer">
                        Mark as Hollywood Trucks internal user
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Internal users have full access to every company and can sign in to Sales.
                    </p>
                  </div>
                )}
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company">Company</Label>
                <Select
                  value={formData.company_id}
                  onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                >
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {renderCompanyOptions(companies)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="role">Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({
                    ...formData,
                    role: value,
                    // A company-level role is stored company-wide; carrying group
                    // ticks over would submit a scope the server will not honour.
                    group_ids: isCompanyLevelRole(value) ? [] : formData.group_ids,
                  })}
                >
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableRoles().map(({ value, label }) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {getAvailableRoles().length === 0 && (
                  <p className="text-xs text-amber-500 mt-1">
                    You cannot create users above your role level
                  </p>
                )}
              </div>
            </div>

            {/* Industry Display (Read-Only) */}
            {formData.company_id && (
              <div className="text-xs text-muted-foreground bg-muted/30 px-3 py-2 rounded border border-border/50">
                <span className="font-medium">Industry:</span>{' '}
                {companies.find((a) => a.id === formData.company_id)?.industry || 'Not set'}
              </div>
            )}

            {/* Multi-Group Selection — group-scoped roles only.
                superadmin/admin/company_admin administer the whole company. Letting
                them be pinned to a group produced an admin whose access froze at the
                groups that existed the day they were invited; the server normalises
                these roles to company-wide (api/services/rbac.js), so offering the
                picker here would only misrepresent what is about to be saved. */}
            {isCompanyLevelRole(formData.role) ? (
              <div className="text-xs text-muted-foreground bg-muted/30 px-3 py-2 rounded border border-border/50">
                <span className="font-medium">{ROLE_LABELS[formData.role] || formData.role}</span>{' '}
                covers the entire company — every {getLabel('group').toLowerCase()} and site,
                including ones created later. No group selection needed.
              </div>
            ) : (
            <div>
              <Label className="mb-3 block">Groups / Teams (Optional)</Label>
              <div className="max-h-48 overflow-y-auto space-y-2 bg-card/50 border border-border rounded-lg p-3">
                {formData.company_id ? (
                  (() => {
                    const companyGroups = groups.filter((g) => g.company_id === formData.company_id);
                    const parentGroups = companyGroups.filter((g) => !g.parent_group_id);

                    if (parentGroups.length === 0) {
                      return (
                        <p className="text-sm text-muted-foreground text-center py-2">
                          No groups available for this company
                        </p>
                      );
                    }

                    return parentGroups.map((parentGroup) => {
                      const children = companyGroups.filter((g) => g.parent_group_id === parentGroup.id);
                      const childIds = children.map((c) => c.id);
                      const allChildrenSelected = childIds.length > 0 && childIds.every((id: string) => formData.group_ids.includes(id));
                      const someChildrenSelected = childIds.some((id: string) => formData.group_ids.includes(id));
                      const isParentChecked = formData.group_ids.includes(parentGroup.id) || allChildrenSelected;
                      const isIndeterminate = someChildrenSelected && !allChildrenSelected && !formData.group_ids.includes(parentGroup.id);

                      return (
                        <div key={parentGroup.id}>
                          <div className="flex items-center gap-2 py-1">
                            <Checkbox
                              id={`group-${parentGroup.id}`}
                              checked={isParentChecked}
                              ref={(el) => {
                                if (el) {
                                  (el as HTMLButtonElement).dataset.state = isIndeterminate ? 'indeterminate' : (isParentChecked ? 'checked' : 'unchecked');
                                }
                              }}
                              className={isIndeterminate ? 'opacity-50' : ''}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  // Select parent and all children
                                  const newIds = new Set([...formData.group_ids, parentGroup.id, ...childIds]);
                                  setFormData({ ...formData, group_ids: Array.from(newIds) });
                                } else {
                                  // Deselect parent and all children
                                  const idsToRemove = new Set([parentGroup.id, ...childIds]);
                                  setFormData({ ...formData, group_ids: formData.group_ids.filter((id: string) => !idsToRemove.has(id)) });
                                }
                              }}
                            />
                            <Label htmlFor={`group-${parentGroup.id}`} className="font-medium cursor-pointer">
                              {parentGroup.name}
                              {childIds.length > 0 && (
                                <span className="text-xs text-muted-foreground ml-2">
                                  ({childIds.length} site{childIds.length > 1 ? 's' : ''})
                                </span>
                              )}
                            </Label>
                          </div>
                          {children.map((childGroup) => (
                            <div key={childGroup.id} className="flex items-center gap-2 py-1 pl-6">
                              <Checkbox
                                id={`group-${childGroup.id}`}
                                checked={formData.group_ids.includes(childGroup.id)}
                                onCheckedChange={(checked) => {
                                  let newIds: string[];
                                  if (checked) {
                                    newIds = [...formData.group_ids, childGroup.id];
                                    // If all children are now selected, also select parent
                                    const willAllBeSelected = childIds.every((id: string) =>
                                      id === childGroup.id || formData.group_ids.includes(id)
                                    );
                                    if (willAllBeSelected && !formData.group_ids.includes(parentGroup.id)) {
                                      newIds.push(parentGroup.id);
                                    }
                                  } else {
                                    // Deselect child and parent (since not all children selected)
                                    newIds = formData.group_ids.filter((id: string) =>
                                      id !== childGroup.id && id !== parentGroup.id
                                    );
                                  }
                                  setFormData({ ...formData, group_ids: newIds });
                                }}
                              />
                              <Label htmlFor={`group-${childGroup.id}`} className="text-sm cursor-pointer text-muted-foreground">
                                └ {childGroup.name}
                              </Label>
                            </div>
                          ))}
                        </div>
                      );
                    });
                  })()
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    Select a company first
                  </p>
                )}
              </div>
              {formData.group_ids.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.group_ids.length} group{formData.group_ids.length > 1 ? 's' : ''} selected
                </p>
              )}
            </div>
            )}

            {/* Personal Message (Invitations only) */}
            {formData.is_invite && (
              <div>
                <Label htmlFor="message">Personal Message (Optional)</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Add a personal note to include in the invitation email..."
                  className="bg-background border-border min-h-[80px] resize-none"
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground mt-1 text-right">
                  {formData.message.length}/500
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button type="submit" className="flex-1 bg-[#8FB560] hover:bg-[#7a9d51]">
              {formData.is_invite ? <><Mail className="w-4 h-4 mr-2" /> Send Invitation</> : 'Create User'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={closeCreateDrawer}
              className="border-border hover:bg-muted"
            >
              Cancel
            </Button>
          </div>
        </form>
      </AdminFormDrawer>

      {/* Bulk Edit Drawer */}
      <AdminFormDrawer
        open={bulkDrawerOpen}
        onClose={() => setBulkDrawerOpen(false)}
        title={`Bulk Edit: ${selectedUsers.length} Users`}
        description="Update multiple users at once"
      >
        <form onSubmit={handleBulkSubmit} className="space-y-4">
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-xs text-blue-400">
            Editing {selectedUsers.length} users. Only filled fields will be updated.
          </div>

          <div>
            <Label htmlFor="bulk_role">Role</Label>
            <Select
              value={bulkFormData.role}
              onValueChange={(value) => setBulkFormData({ ...bulkFormData, role: value })}
            >
              <SelectTrigger className="bg-background border-border">
                <SelectValue placeholder="No change" />
              </SelectTrigger>
              <SelectContent>
                {getAvailableRoles(true).map(({ value, label }) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="bulk_group">Group / Team</Label>
            <Select
              value={bulkFormData.group_id}
              onValueChange={(value) => setBulkFormData({ ...bulkFormData, group_id: value })}
            >
              <SelectTrigger className="bg-background border-border">
                <SelectValue placeholder="No change" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__no_change__">No change</SelectItem>
                {renderGroupOptions(groups)}
              </SelectContent>
            </Select>
          </div>

          {is_superadmin && (
            <div className="flex items-center space-x-2 bg-card p-3 rounded-lg border border-border">
              <input
                type="checkbox"
                id="bulk_is_internal"
                checked={bulkFormData.is_internal}
                onChange={(e) =>
                  setBulkFormData({ ...bulkFormData, is_internal: e.target.checked })
                }
                className="rounded border-border"
              />
              <Label htmlFor="bulk_is_internal" className="font-normal cursor-pointer">
                Mark as Hollywood Trucks internal users
              </Label>
            </div>
          )}

          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 text-xs text-yellow-400">
            This will update {selectedUsers.length} users. Empty fields will not be changed.
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" className="flex-1 bg-[#8FB560] hover:bg-[#7a9d51]">
              Update {selectedUsers.length} Users
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setBulkDrawerOpen(false)}
              className="border-border hover:bg-muted"
            >
              Cancel
            </Button>
          </div>
        </form>
      </AdminFormDrawer>

      {/* Edit User Drawer */}
      <AdminFormDrawer
        open={editDrawerOpen}
        onClose={() => {
          setEditDrawerOpen(false);
          setEditingUser(null);
        }}
        title="Edit User"
        description="Update user profile information"
      >
        <form onSubmit={handleEditSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit_email">Email Address</Label>
              <Input
                id="edit_email"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                placeholder="user@example.com"
                required
                className="bg-background border-border"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_first_name">First Name</Label>
                <Input
                  id="edit_first_name"
                  value={editFormData.first_name}
                  onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
                  placeholder="John"
                  className="bg-background border-border"
                />
              </div>
              <div>
                <Label htmlFor="edit_last_name">Last Name</Label>
                <Input
                  id="edit_last_name"
                  value={editFormData.last_name}
                  onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
                  placeholder="Doe"
                  className="bg-background border-border"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit_job_title">Title</Label>
              <Input
                id="edit_job_title"
                autoComplete="organization-title"
                maxLength={80}
                value={editFormData.job_title}
                onChange={(e) => setEditFormData({ ...editFormData, job_title: e.target.value })}
                placeholder="Fleet Operations Manager"
                className="bg-background border-border"
              />
            </div>

            <div>
              <Label htmlFor="edit_phone">Phone Number</Label>
              <Input
                id="edit_phone"
                type="tel"
                autoComplete="tel"
                value={editFormData.phone}
                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                placeholder="(310) 555-1212"
                className="bg-background border-border"
              />
            </div>

            {is_superadmin && (
              <div className="flex items-center space-x-2 bg-card p-3 rounded-lg border border-border">
                <input
                  type="checkbox"
                  id="edit_is_internal"
                  checked={editFormData.is_internal}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, is_internal: e.target.checked })
                  }
                  className="rounded border-border"
                />
                <Label htmlFor="edit_is_internal" className="font-normal cursor-pointer">
                  Mark as Hollywood Trucks internal user
                </Label>
              </div>
            )}

            {/* T-018: per-employee wearables grant — superadmin or internal HT staff only. */}
            {(is_superadmin || currentUser?.is_internal) && (
              <div className="flex items-center space-x-2 bg-card p-3 rounded-lg border border-border">
                <input
                  type="checkbox"
                  id="edit_wearables_enabled"
                  checked={editFormData.wearables_enabled}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, wearables_enabled: e.target.checked })
                  }
                  className="rounded border-border"
                />
                <Label htmlFor="edit_wearables_enabled" className="font-normal cursor-pointer flex items-center gap-2">
                  <Glasses className="w-4 h-4" style={{ color: '#8FB560' }} />
                  Allow this employee to operate wearables
                </Label>
              </div>
            )}

            <div>
              <Label htmlFor="edit_default_company">Default Company</Label>
              <Select
                value={editFormData.default_company_id}
                onValueChange={(value) =>
                  setEditFormData({ ...editFormData, default_company_id: value })
                }
              >
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder="Select default company..." />
                </SelectTrigger>
                <SelectContent>
                  {renderCompanyOptions(companies)}
                </SelectContent>
              </Select>
              {/* Industry Display (Read-Only) */}
              {editFormData.default_company_id && (
                <div className="text-xs text-muted-foreground mt-1.5">
                  Industry: {companies.find((a) => a.id === editFormData.default_company_id)?.industry || 'Not set'}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="edit_default_group">Default Group</Label>
              <Select
                value={editFormData.default_group_id || "__none__"}
                onValueChange={(value) =>
                  setEditFormData({ ...editFormData, default_group_id: value === "__none__" ? "" : value })
                }
              >
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder="Select default group..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {renderGroupOptions(groups, editFormData.default_company_id)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Activity History */}
          {editingUser && (
            <EntityAuditTimeline
              entityType="user"
              entityId={editingUser.id}
              entityName={editingUser.first_name ? `${editingUser.first_name} ${editingUser.last_name || ''}`.trim() : editingUser.email}
            />
          )}

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button type="submit" className="flex-1 bg-[#8FB560] hover:bg-[#7a9d51]">
              <CheckCircle className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEditDrawerOpen(false);
                setEditingUser(null);
              }}
              className="border-border hover:bg-muted"
            >
              Cancel
            </Button>
          </div>
        </form>
      </AdminFormDrawer>

      {/* Password Reset Drawer (Superadmin Only) */}
      {is_superadmin && (
        <AdminFormDrawer
          open={passwordResetDrawerOpen}
          onClose={() => {
            setPasswordResetDrawerOpen(false);
            setPasswordResetUser(null);
            setNewPassword('');
            setConfirmPassword('');
          }}
          title="Reset User Password"
          description={passwordResetUser ? `Set a new password for ${getDisplayName(passwordResetUser)}` : ''}
        >
          <form onSubmit={handlePasswordResetSubmit} className="space-y-6">
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 text-xs text-yellow-400">
              <strong>Warning:</strong> This action will set a new password for the user. They will need to use this new password to log in. The user will not receive an email notification.
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="new_password">New Password</Label>
                <PasswordInput
                  id="new_password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  className="bg-background"
                />
              </div>

              {/* Password Requirements (informational for superadmins - not enforced) */}
              <div className="bg-card/50 border border-border rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-2 font-medium">Password Strength (not enforced for superadmins):</p>
                <div className="space-y-1.5">
                  {passwordRequirements.map((req, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      {req.met ? (
                        <CheckCircle className="w-3.5 h-3.5 text-[#8FB560]" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full border border-border" />
                      )}
                      <span className={req.met ? 'text-foreground' : 'text-muted-foreground'}>
                        {req.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="reset_confirm_password">Confirm Password</Label>
                <PasswordInput
                  id="reset_confirm_password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  className="bg-background"
                  error={confirmPassword.length > 0 && newPassword !== confirmPassword}
                />
              </div>

              {/* Password Match Indicator */}
              {confirmPassword && (
                <div className="flex items-center gap-2 text-xs">
                  {newPassword === confirmPassword ? (
                    <>
                      <CheckCircle className="w-3.5 h-3.5 text-[#8FB560]" />
                      <span className="text-foreground">Passwords match</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-3.5 h-3.5 text-red-400" />
                      <span className="text-red-400">Passwords do not match</span>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                type="submit"
                className="flex-1 bg-[#8FB560] hover:bg-[#7a9d51]"
                disabled={!newPassword || newPassword !== confirmPassword}
              >
                <Key className="w-4 h-4 mr-2" />
                Reset Password
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setPasswordResetDrawerOpen(false);
                  setPasswordResetUser(null);
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="border-border hover:bg-muted"
              >
                Cancel
              </Button>
            </div>
          </form>
        </AdminFormDrawer>
      )}

      {/* Membership Management Drawer */}
      <AdminFormDrawer
        open={membershipDrawerOpen}
        onClose={() => {
          setMembershipDrawerOpen(false);
          setManagingUser(null);
        }}
        title="Manage Access"
        description={managingUser ? `Control ${getDisplayName(managingUser)}'s company and group memberships` : 'Manage user access'}
      >
        <div className="space-y-6">
          {/* Current Memberships */}
          <div>
            <Label className="text-base mb-3 block">Current Memberships</Label>
            <div className="space-y-3">
              {managingUser?.memberships.map((membership) => (
                <div
                  key={membership.id}
                  className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border"
                >
                  <div className="flex-1">
                    <div className="font-medium text-foreground">{membership.company_name}</div>
                    {membership.group_name && (
                      <div className="text-sm text-muted-foreground">{membership.group_name}</div>
                    )}
                  </div>
                  <Select
                    value={membership.role}
                    onValueChange={(value) => handleUpdateMembershipRole(membership.id, value)}
                  >
                    <SelectTrigger className="w-[140px] bg-background border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableRolesShort().map(({ value, label }) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteMembership(membership.id)}
                    className="border-red-500/20 text-red-400 hover:bg-red-500/10"
                    disabled={managingUser.memberships.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            {managingUser && managingUser.memberships.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-4">
                No memberships. Add one below.
              </div>
            )}
          </div>

          {/* Add New Membership */}
          <div className="border-t border-border pt-6">
            <Label className="text-base mb-3 block">Add New Membership</Label>
            <div className="space-y-3">
              <div>
                <Label htmlFor="new_company">Company</Label>
                <Select
                  value={newMembershipForm.company_id}
                  onValueChange={(value) =>
                    setNewMembershipForm({ ...newMembershipForm, company_id: value, group_id: '' })
                  }
                >
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue placeholder="Select company..." />
                  </SelectTrigger>
                  <SelectContent>
                    {renderCompanyOptions(companies)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="new_group">Group (Optional)</Label>
                <Select
                  value={newMembershipForm.group_id || "__none__"}
                  onValueChange={(value) =>
                    setNewMembershipForm({ ...newMembershipForm, group_id: value === "__none__" ? "" : value })
                  }
                >
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue placeholder="None (Company Level)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None (Company Level)</SelectItem>
                    {renderGroupOptions(groups, newMembershipForm.company_id)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="new_role">Role</Label>
                <Select
                  value={newMembershipForm.role}
                  onValueChange={(value) =>
                    setNewMembershipForm({ ...newMembershipForm, role: value })
                  }
                >
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableRoles().map(({ value, label }) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleAddMembership}
                disabled={!newMembershipForm.company_id}
                className="w-full bg-[#8FB560] hover:bg-[#7a9d51]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Membership
              </Button>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setMembershipDrawerOpen(false);
                setManagingUser(null);
              }}
              className="flex-1 border-border hover:bg-muted"
            >
              Done
            </Button>
          </div>
        </div>
      </AdminFormDrawer>

      {/* Team Members Management Drawer */}
      <AdminFormDrawer
        open={teamMembersDrawerOpen}
        onClose={() => {
          setTeamMembersDrawerOpen(false);
          setSelectedTeam(null);
        }}
        title={selectedTeam ? `${selectedTeam.name} Members` : 'Team Members'}
        description="View and manage team membership"
      >
        <div className="space-y-6">
          {/* Add Member Button */}
          <Button
            onClick={handleOpenAddToTeam}
            className="w-full bg-[#8FB560] hover:bg-[#7a9d51]"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Member to Team
          </Button>

          {/* Current Members List */}
          <div className="space-y-3">
            <Label className="text-base">Current Members</Label>
            {selectedTeam && getTeamMembers(selectedTeam.id).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No members in this team yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedTeam && getTeamMembers(selectedTeam.id).map((user) => {
                  const membership = user.memberships.find(m => m.group_id === selectedTeam.id);
                  return (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 bg-card rounded-lg border border-border"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium text-foreground">{getDisplayName(user)}</span>
                        <span className="text-xs text-muted-foreground">{user.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-muted text-foreground border-border whitespace-nowrap">
                          {ROLE_LABELS_SHORT[membership?.role || 'user'] || membership?.role}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFromTeam(user, selectedTeam.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setTeamMembersDrawerOpen(false);
                setSelectedTeam(null);
              }}
              className="flex-1 border-border hover:bg-muted"
            >
              Done
            </Button>
          </div>
        </div>
      </AdminFormDrawer>

      {/* Add User to Team Drawer */}
      <AdminFormDrawer
        open={addToTeamDrawerOpen}
        onClose={() => setAddToTeamDrawerOpen(false)}
        title={selectedTeam ? `Add Member to ${selectedTeam.name}` : 'Add Member'}
        description="Select a user to add to this team"
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="add_user">Select User</Label>
            <Select
              value={addToTeamForm.user_id}
              onValueChange={(value) => setAddToTeamForm({ ...addToTeamForm, user_id: value })}
            >
              <SelectTrigger className="bg-background border-border">
                <SelectValue placeholder="Choose a user..." />
              </SelectTrigger>
              <SelectContent>
                {selectedTeam && getUsersNotInTeam(selectedTeam.id).map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {getDisplayName(user)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="add_role">Role</Label>
            <Select
              value={addToTeamForm.role}
              onValueChange={(value) => setAddToTeamForm({ ...addToTeamForm, role: value })}
            >
              <SelectTrigger className="bg-background border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getAvailableRoles().map(({ value, label }) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleAddUserToTeam}
              disabled={!addToTeamForm.user_id}
              className="flex-1 bg-[#8FB560] hover:bg-[#7a9d51]"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add to Team
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAddToTeamDrawerOpen(false)}
              className="border-border hover:bg-muted"
            >
              Cancel
            </Button>
          </div>
        </div>
      </AdminFormDrawer>

      {/* Verification Code Modal */}
      <Dialog
        open={verificationCodeModal.open}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setVerificationCodeModal({ open: false, code: '', inviteUrl: '', email: '' });
          }
        }}
      >
        <DialogContent className="sm:max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <CheckCircle className="w-5 h-5 text-[#8FB560]" />
              Invitation Sent Successfully
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              An email has been sent to <span className="text-foreground font-medium">{verificationCodeModal.email}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Verification Code */}
            <div className="bg-background border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm text-muted-foreground">Verification Code</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(verificationCodeModal.code);
                    toast.success('Code copied to clipboard');
                  }}
                  className="h-7 px-2 text-muted-foreground hover:text-foreground"
                >
                  <Copy className="w-3.5 h-3.5 mr-1" />
                  Copy
                </Button>
              </div>
              <div className="flex gap-2 justify-center">
                {verificationCodeModal.code.split('').map((char, index) => (
                  <div
                    key={index}
                    className="w-12 h-14 flex items-center justify-center text-2xl font-mono font-bold bg-card border border-border rounded-lg text-[#8FB560]"
                  >
                    {char}
                  </div>
                ))}
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
              <div className="flex gap-2">
                <Shield className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-300">
                  <p className="font-medium mb-1">Share this code separately</p>
                  <p className="text-amber-400/80">
                    Send this 4-digit code to the invitee via text, phone call, or another secure channel.
                    They will need it to complete their registration.
                  </p>
                </div>
              </div>
            </div>

            {/* Invite URL (for reference) */}
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">Invitation Link (included in email)</Label>
              <div className="flex gap-2">
                <Input
                  value={verificationCodeModal.inviteUrl}
                  readOnly
                  className="bg-background border-border text-muted-foreground text-xs font-mono"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(verificationCodeModal.inviteUrl);
                    toast.success('Link copied to clipboard');
                  }}
                  className="border-border hover:bg-muted shrink-0"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => setVerificationCodeModal({ open: false, code: '', inviteUrl: '', email: '' })}
              className="flex-1 bg-[#8FB560] hover:bg-[#7a9d51]"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Teams-tab CRUD: create / edit a team (a root group). Reuses the group APIs. */}
      <AdminFormDrawer
        open={teamDrawerOpen}
        onClose={() => { if (!teamSaving) setTeamDrawerOpen(false); }}
        title={editingTeam ? 'Edit Team' : 'Add Team'}
        description={editingTeam ? 'Rename the team. Company can only be set when creating.' : 'Create a new team (a top-level group).'}
      >
        <form onSubmit={handleTeamSubmit} className="space-y-4">
          <div>
            <Label htmlFor="team-name">Team Name</Label>
            <Input
              id="team-name"
              value={teamForm.name}
              onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
              placeholder="e.g. Production A"
              required
              className="bg-background border-border"
            />
          </div>
          <div>
            <Label htmlFor="team-code">Code / Identifier</Label>
            <Input
              id="team-code"
              value={teamForm.code}
              onChange={(e) => setTeamForm({ ...teamForm, code: e.target.value.toUpperCase() })}
              placeholder="e.g. PROD-A (optional)"
              className="bg-background border-border font-mono"
            />
          </div>
          <CallsignsField
            id="team-callsigns"
            value={teamForm.callsigns}
            onChange={(callsigns) => setTeamForm({ ...teamForm, callsigns })}
            example="SDM"
          />
          <div>
            <Label htmlFor="team-company">Company</Label>
            <Select
              value={teamForm.company_id}
              onValueChange={(v) => setTeamForm({ ...teamForm, company_id: v })}
              disabled={!!editingTeam}
            >
              <SelectTrigger id="team-company" className="bg-background border-border">
                <SelectValue placeholder="Select a company" />
              </SelectTrigger>
              <SelectContent>
                {renderCompanyOptions(companies)}
              </SelectContent>
            </Select>
            {editingTeam && (
              <p className="mt-1 text-xs text-muted-foreground">Company is fixed after creation.</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" disabled={teamSaving} onClick={() => setTeamDrawerOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={teamSaving} className="bg-[#8FB560] hover:bg-[#7a9d51]">
              {teamSaving ? 'Saving…' : editingTeam ? 'Save changes' : 'Create team'}
            </Button>
          </div>
        </form>
      </AdminFormDrawer>
    </div>
  );
}
